import { Divider, Stack, Typography } from '@mui/material';
import type { Provider, ResourceType } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { useDebouncedValue } from '@/utils/useDebouncedValue';

import { ResourcesSelectorAccordion } from './resourcesSelector';

import { useResourceIdentifiers } from '../queryBuilder/useResourceIentifiers';

export const ResourceSelectionManualOverridesEditor = ({
  description,
  includeResources,
  excludeResources,
  setIncludedResources,
  setExcludedResources,
  supportedProviders,
  supportedResourceTypes,
}: {
  description: string;
  includeResources: string[];
  excludeResources: string[];
  setIncludedResources: (includedResources: string[]) => void;
  setExcludedResources: (excludedResources: string[]) => void;
  supportedProviders?: Provider[];
  supportedResourceTypes?: ResourceType[];
}) => {
  const [includeFilter, setIncludeFilter] = useState('');
  const [excludeFilter, setExcludeFilter] = useState('');
  const debouncedIncludeFilter = useDebouncedValue(includeFilter, 300);
  const debouncedExcludeFilter = useDebouncedValue(excludeFilter, 300);

  const { data: allResourcesData } = useResourceIdentifiers(
    supportedProviders,
    supportedResourceTypes
  );

  const { data: includeResourcesData } = useResourceIdentifiers(
    supportedProviders,
    supportedResourceTypes,
    debouncedIncludeFilter || undefined
  );
  const { data: excludeResourcesData } = useResourceIdentifiers(
    supportedProviders,
    supportedResourceTypes,
    debouncedExcludeFilter || undefined
  );

  const allResources = allResourcesData?.resources ?? [];

  return (
    <Stack gap='32px' width='308px'>
      <Typography variant='h3'>Manual overrides</Typography>
      <Stack gap='24px' overflow='auto'>
        <Typography variant='body1'>{description}</Typography>
        <Stack gap='12px'>
          <ResourcesSelectorAccordion
            label='Included resources'
            description={`${includeResources.length} resources manually included${includeResources.length ? ':' : '.'}`}
            allResources={allResources}
            filteredResources={includeResourcesData?.resources ?? []}
            resources={includeResources}
            onFilterChange={setIncludeFilter}
            addResource={(resource) =>
              setIncludedResources([...includeResources, resource])
            }
            removeResource={(resource) =>
              setIncludedResources(
                includeResources.filter((r) => r !== resource)
              )
            }
          />
          <Divider />
          <ResourcesSelectorAccordion
            label='Excluded resources'
            description={`${excludeResources.length} resources manually excluded:`}
            allResources={allResources}
            filteredResources={excludeResourcesData?.resources ?? []}
            resources={excludeResources}
            onFilterChange={setExcludeFilter}
            addResource={(resource) =>
              setExcludedResources([...excludeResources, resource])
            }
            removeResource={(resource) =>
              setExcludedResources(
                excludeResources.filter((r) => r !== resource)
              )
            }
          />
          <Divider />
        </Stack>
      </Stack>
    </Stack>
  );
};
